import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { ToolbarAppsConfig, ToolbarNotification } from './notificationTypes';
import { ToolbarEchoGate } from './ToolbarEchoGate';
import { ToolbarNotificationsBroadcast } from './ToolbarNotificationsBroadcast';
import { ToolbarNotificationsList } from './ToolbarNotificationsList';
import type { ToolbarEchoParams } from './getToolbarEchoConfig';
import { dedupeNotificationsById, removeRecurrentNotificationsThatAlreadyPassed, shouldShowNotificationForFilter } from './notificationModel';

export type ToolbarNotificationsPanelProps = {
    initialNotifications: ToolbarNotification[];
    filterApplicationCode: string;
    appName: string;
    userId: string | number | null | undefined;
    /** When set with `userId`, enables Echo; omit or null to render list only */
    echoParams: ToolbarEchoParams | null | undefined;
    appsConfig: ToolbarAppsConfig | undefined;
    translate: (key: string) => string;
    resolveAsset: (path: string) => string;
    /** When true, `onUnreadBellBump` is not called (e.g. notifications drawer is open) */
    skipUnreadBellBump?: boolean;
    onGlobalNotification?: (notification: ToolbarNotification) => void;
    onUnreadBellBump?: () => void;
    /** When false, no Echo subscription is created (parent can subscribe outside, e.g. while drawer is closed). Default true. */
    subscribeToEcho?: boolean;
    emptyContent?: ReactNode;
    className?: string;
};

function mergeIncoming(prev: ToolbarNotification[], incoming: ToolbarNotification): ToolbarNotification[] {
    const next = [incoming, ...prev];
    return dedupeNotificationsById(next);
}

/**
 * Notifications list + optional Laravel Echo subscription (same channel/event as the Plus toolbar).
 */
export function ToolbarNotificationsPanel({ initialNotifications, filterApplicationCode, appName, userId, echoParams, appsConfig, translate, resolveAsset, skipUnreadBellBump, onGlobalNotification, onUnreadBellBump, subscribeToEcho = true, emptyContent, className }: ToolbarNotificationsPanelProps) {
    const [items, setItems] = useState<ToolbarNotification[]>(() => dedupeNotificationsById(removeRecurrentNotificationsThatAlreadyPassed(initialNotifications)));

    useEffect(() => {
        setItems(dedupeNotificationsById(removeRecurrentNotificationsThatAlreadyPassed(initialNotifications)));
    }, [initialNotifications]);

    const onEchoPayload = useCallback(
        (e: ToolbarNotification) => {
            if (e.type === 'global') {
                onGlobalNotification?.(e);
            }
            if (!skipUnreadBellBump) {
                onUnreadBellBump?.();
            }
            setItems((prev) => mergeIncoming(prev, e));
        },
        [onGlobalNotification, onUnreadBellBump, skipUnreadBellBump],
    );

    const visible = useMemo(() => removeRecurrentNotificationsThatAlreadyPassed(items).filter((n) => shouldShowNotificationForFilter(n, filterApplicationCode)), [items, filterApplicationCode]);

    const listBody =
        visible.length === 0 ? (
            (emptyContent ?? <div className='rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 px-4 py-8 text-center text-sm text-gray-500'>{translate('Nenhuma Notificação')}</div>)
        ) : (
            <ToolbarNotificationsList items={visible} appsConfig={appsConfig} translate={translate} resolveAsset={resolveAsset} className={className} />
        );

    const listViewKey = `${filterApplicationCode || '__all__'}:${visible.length === 0 ? 'empty' : 'list'}`;

    const animatedListBody = (
        <AnimatePresence mode='wait' initial={false}>
            <motion.div key={listViewKey} className='w-full' initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>
                {listBody}
            </motion.div>
        </AnimatePresence>
    );

    const canEcho = Boolean(echoParams && userId != null && userId !== '' && appName);

    if (!canEcho || !subscribeToEcho) {
        return animatedListBody;
    }

    const echo = echoParams!;

    return (
        <ToolbarEchoGate params={echo}>
            <ToolbarNotificationsBroadcast appName={appName} userId={userId!} onNotification={onEchoPayload} />
            {animatedListBody}
        </ToolbarEchoGate>
    );
}
